// The squad settings screen's write state (#6780): which squad the modal is authoring,
// the save, the delete confirm, and where a refusal renders. Split from
// `components/SquadSettings.tsx` the way `createAgentConfigEditor` is: a form with a
// re-entrancy guard, an inline refusal and a separate destructive confirm is STATE,
// not rendering.
//
// Every write re-reads the list on BOTH outcomes (`onSettled`), never patching from the
// echo: a success is what the server did, and a refusal may mean the list is stale
// (a squad deleted elsewhere answers 404, a repo moved elsewhere answers 409).

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { createConfirmGate } from './confirm-gate';
import { createMutation } from './mutation';
import {
  createSquad,
  deleteSquad,
  type NamedSquad,
  type SquadWire,
  SquadWriteError,
  updateSquad,
} from './squads';
import type { ToastTone } from './toast';

/** What the modal is authoring: a new squad, an existing one, or nothing (closed). */
export type SquadTarget = NamedSquad | 'new' | null;

/** The modal's fields as typed: the raw name and the ticked repos' full names. */
export type SquadDraft = { readonly name: string; readonly repos: readonly string[] };

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((r) => b.includes(r));

/**
 * The PATCH body for an edit: only the half that changed. `null` when nothing did, which
 * the route would refuse (it needs `name`, `repos` or both), so the modal offers no save.
 */
export function squadPatch(
  squad: NamedSquad,
  draft: SquadDraft,
): { name?: string; repos?: readonly string[] } | null {
  const renamed = draft.name.trim() !== squad.name;
  const regrouped = !sameSet(draft.repos, squad.repos);
  if (!renamed && !regrouped) return null;
  return {
    ...(renamed ? { name: draft.name } : {}),
    ...(regrouped ? { repos: draft.repos } : {}),
  };
}

export function createSquadEditor(input: {
  readonly token: string | null;
  /** Re-read the list. Awaited inside the write's pending window. */
  readonly reread: () => unknown;
  readonly notify: (message: string, tone?: ToastTone) => void;
}) {
  const [editing, setEditing] = createSignal<SquadTarget>(null);
  const [nameError, setNameError] = createSignal<string | null>(null);
  const [formError, setFormError] = createSignal<string | null>(null);

  const clearErrors = (): void => {
    setNameError(null);
    setFormError(null);
  };

  const write = async (target: NamedSquad | 'new', draft: SquadDraft): Promise<SquadWire> => {
    if (target === 'new') return createSquad(draft, input.token);
    const patch = squadPatch(target, draft);
    // The modal offers no save over an unchanged squad; a stray submit still sends nothing.
    if (patch === null) return target;
    return updateSquad(target.id, patch, input.token);
  };

  const save = createMutation({
    call: async (target: NamedSquad | 'new', draft: SquadDraft) => {
      clearErrors();
      await write(target, draft);
      return target === 'new' ? t('squads.created') : t('squads.saved');
    },
    fallback: t('squads.write_failed'),
    onSuccess: (notice) => {
      setEditing(null);
      input.notify(notice);
    },
    onError: (message, cause) => {
      if (cause instanceof SquadWriteError && cause.field === 'name') setNameError(message);
      else setFormError(message);
    },
    onSettled: () => input.reread(),
  });

  const remove = createMutation({
    call: (squad: NamedSquad) => deleteSquad(squad.id, input.token),
    fallback: t('squads.delete_failed'),
    onSuccess: () => input.notify(t('squads.deleted')),
    onError: (message) => input.notify(message, 'error'),
    onSettled: () => input.reread(),
  });
  const deleting = createConfirmGate<NamedSquad>({ act: (squad) => remove.run(squad) });

  return {
    editing,
    nameError,
    formError,
    pending: save.pending,
    /** Open the modal: no argument creates, a squad edits it. Clears any refusal. */
    open: (squad?: NamedSquad): void => {
      clearErrors();
      setEditing(() => squad ?? 'new');
    },
    close: (): void => {
      setEditing(null);
    },
    /** Save the draft for the open target. Dropped while one is in flight. */
    submit: (draft: SquadDraft): void => {
      const target = editing();
      if (target !== null) void save.run(target, draft);
    },
    deleting,
  };
}

export type SquadEditor = ReturnType<typeof createSquadEditor>;
